// ASTRA Resource System - Tiered resources from astronomical sources
// Categories: Ore (asteroids), Ice (ice asteroids), Gas (nebulae), Organic/Crystal (planets), Solar (suns)
// Each category has T1-T4 tiers with increasing value

// NOTE: Asteroid ores use the legacy RESOURCES constant in constants.ts (uppercase keys)
// The new resource types below are for ice asteroids, nebulae, and refined materials

// ==================== ICE RESOURCES ====================
// Source: Ice asteroids (static harvestable fields)
// Used for: Fuel, coolant, shields

export const ICE_RESOURCES = {
  water_ice: { tier: 1, value: 12, name: 'Water Ice', color: 0xcceeff },
  ammonia: { tier: 2, value: 30, name: 'Ammonia', color: 0x9bb8d3 },
  methane: { tier: 3, value: 80, name: 'Methane', color: 0x88ddff },
  helium3: { tier: 4, value: 250, name: 'Helium-3', color: 0xffffcc },
} as const;

export type IceType = keyof typeof ICE_RESOURCES;
export const ICE_TYPES = Object.keys(ICE_RESOURCES) as IceType[];

// ==================== GAS RESOURCES ====================
// Source: Nebulae (large gas clouds)
// Used for: Propulsion, special items

export const GAS_RESOURCES = {
  hydrogen: { tier: 1, value: 8, name: 'Hydrogen', color: 0xccffcc },
  nitrogen: { tier: 2, value: 20, name: 'Nitrogen', color: 0xaaddff },
  xenon: { tier: 3, value: 60, name: 'Xenon', color: 0xbb99ff },
  antimatter: { tier: 4, value: 200, name: 'Antimatter', color: 0xff00ff },
} as const;

export type GasType = keyof typeof GAS_RESOURCES;
export const GAS_TYPES = Object.keys(GAS_RESOURCES) as GasType[];

// ==================== REFINED RESOURCES ====================
// Source: Refineries (crafted from ore + ice)
// Used for: Equipment, ships

export const REFINED_RESOURCES = {
  steel_alloy: { tier: 1, value: 35, name: 'Steel Alloy', color: 0x71797e },
  cryo_bronze: { tier: 2, value: 85, name: 'Cryo-Bronze', color: 0xcd7f32 },
  carbide_plate: { tier: 3, value: 250, name: 'Carbide Plate', color: 0x36454f },
  quantum_alloy: { tier: 4, value: 700, name: 'Quantum Alloy', color: 0x00ffff },
} as const;

export type RefinedType = keyof typeof REFINED_RESOURCES;
export const REFINED_TYPES = Object.keys(REFINED_RESOURCES) as RefinedType[];

// ==================== ORGANIC RESOURCES ====================
// Source: planet-surface mining stations (via the planet management panel).
// Used for: biological synthesis, bio-armor, organic compounds (recipes TBD).

export const ORGANIC_RESOURCES = {
  biomass:          { tier: 1, value: 15,  name: 'Biomass',          color: 0x7cb342 },
  polymers:         { tier: 2, value: 40,  name: 'Polymers',         color: 0x558b2f },
  nanocarbon:       { tier: 3, value: 120, name: 'Nanocarbon',       color: 0x2e7d32 },
  exotic_compounds: { tier: 4, value: 400, name: 'Exotic Compounds', color: 0x1b5e20 },
} as const;

export type OrganicType = keyof typeof ORGANIC_RESOURCES;
export const ORGANIC_TYPES = Object.keys(ORGANIC_RESOURCES) as OrganicType[];

// ==================== CRYSTAL RESOURCES ====================
// Source: planet-surface mining stations (via the planet management panel).
// Used for: shields, scanners, computation (recipes TBD).

export const CRYSTAL_RESOURCES = {
  quartz:             { tier: 1, value: 18,  name: 'Quartz',             color: 0xe1f5fe },
  silicates:          { tier: 2, value: 45,  name: 'Silicates',          color: 0x81d4fa },
  rare_crystals:      { tier: 3, value: 140, name: 'Rare Crystals',      color: 0x29b6f6 },
  singularity_shards: { tier: 4, value: 450, name: 'Singularity Shards', color: 0x0277bd },
} as const;

export type CrystalType = keyof typeof CRYSTAL_RESOURCES;
export const CRYSTAL_TYPES = Object.keys(CRYSTAL_RESOURCES) as CrystalType[];

// ==================== SOLAR RESOURCES ====================
// Source: Suns (harvested inside the radiation zone — high risk, high reward).
// Used for: energy cells, advanced propulsion, shields, end-game crafting.

export const SOLAR_RESOURCES = {
  solar_plasma:   { tier: 1, value: 20,  name: 'Solar Plasma',   color: 0xffcc44 },
  charged_ions:   { tier: 2, value: 55,  name: 'Charged Ions',   color: 0xffaa22 },
  fusion_matter:  { tier: 3, value: 160, name: 'Fusion Matter',  color: 0xff7711 },
  stellar_core:   { tier: 4, value: 500, name: 'Stellar Core',   color: 0xff4400 },
} as const;

export type SolarType = keyof typeof SOLAR_RESOURCES;
export const SOLAR_TYPES = Object.keys(SOLAR_RESOURCES) as SolarType[];

// ==================== COMBINED TYPES ====================

// New resource type (excludes legacy ore - use ResourceType from constants.ts for asteroids)
export type NewResourceType = IceType | GasType | RefinedType | OrganicType | CrystalType | SolarType;

// All new resources (harvestable from ice asteroids, nebulae, planets, suns)
export const NEW_RAW_RESOURCES = {
  ...ICE_RESOURCES,
  ...GAS_RESOURCES,
  ...ORGANIC_RESOURCES,
  ...CRYSTAL_RESOURCES,
  ...SOLAR_RESOURCES,
} as const;

// All new resources including refined
export const NEW_RESOURCES = {
  ...NEW_RAW_RESOURCES,
  ...REFINED_RESOURCES,
} as const;

// Resource category lookup (for new resources only)
export type NewResourceCategory = 'ice' | 'gas' | 'refined' | 'organic' | 'crystal' | 'solar';

export function getNewResourceCategory(resourceType: NewResourceType): NewResourceCategory {
  if (resourceType in ICE_RESOURCES) return 'ice';
  if (resourceType in GAS_RESOURCES) return 'gas';
  if (resourceType in ORGANIC_RESOURCES) return 'organic';
  if (resourceType in CRYSTAL_RESOURCES) return 'crystal';
  if (resourceType in SOLAR_RESOURCES) return 'solar';
  return 'refined';
}

export function getNewResourceInfo(resourceType: NewResourceType) {
  return NEW_RESOURCES[resourceType];
}

export function getNewResourceTier(resourceType: NewResourceType): number {
  return NEW_RESOURCES[resourceType].tier;
}

// Get resources by tier for a specific category
export function getResourcesByTier(category: NewResourceCategory, tier: number): NewResourceType[] {
  let resources: Record<string, { tier: number }>;
  switch (category) {
    case 'ice':
      resources = ICE_RESOURCES;
      break;
    case 'gas':
      resources = GAS_RESOURCES;
      break;
    case 'refined':
      resources = REFINED_RESOURCES;
      break;
    case 'organic':
      resources = ORGANIC_RESOURCES;
      break;
    case 'crystal':
      resources = CRYSTAL_RESOURCES;
      break;
    case 'solar':
      resources = SOLAR_RESOURCES;
      break;
  }
  return Object.entries(resources)
    .filter(([, info]) => info.tier === tier)
    .map(([key]) => key as NewResourceType);
}

// ==================== TIER WEIGHTS ====================
// Weight distribution for spawning resources by tier
// Higher tiers are rarer

export const TIER_WEIGHTS = {
  1: 0.50, // 50% T1
  2: 0.30, // 30% T2
  3: 0.15, // 15% T3
  4: 0.05, // 5% T4
} as const;

// Get a random resource type from a category based on tier weights
export function getRandomResourceFromCategory(category: Exclude<NewResourceCategory, 'refined'>): NewResourceType {
  const random = Math.random();
  let cumulative = 0;
  let selectedTier = 1;

  for (const [tier, weight] of Object.entries(TIER_WEIGHTS)) {
    cumulative += weight;
    if (random < cumulative) {
      selectedTier = parseInt(tier);
      break;
    }
  }

  const resources = getResourcesByTier(category, selectedTier);
  return resources[0]; // Each tier has exactly one resource per category
}

// ==================== ORE TIER WEIGHTS ====================
// Asteroid ore distribution by constellation tier
// Uses legacy ResourceType from constants.ts (IRON, COPPER, TITANIUM, PLATINUM)

export const ORE_TIER_WEIGHTS: Record<number, Record<string, number>> = {
  1: { iron: 0.80, copper: 0.20 },
  2: { iron: 0.30, copper: 0.50, titanium: 0.20 },
  3: { copper: 0.20, titanium: 0.50, platinum: 0.30 },
  4: { titanium: 0.30, platinum: 0.70 },
};

// Get ore weights for asteroid generation based on constellation tier
export function getOreWeightsForConstellationTier(constellationTier: number): Record<string, number> {
  return ORE_TIER_WEIGHTS[constellationTier] || ORE_TIER_WEIGHTS[1];
}

// ==================== ICE FIELD TIER WEIGHTS ====================
// Ice asteroid distribution by constellation tier (ice fields = harvestable ice asteroids)
// Mirrors ORE_TIER_WEIGHTS pattern; only non-zero entries returned.

export const ICE_TIER_WEIGHTS: Record<number, Partial<Record<IceType, number>>> = {
  1: { water_ice: 0.80, ammonia: 0.20 },
  2: { water_ice: 0.30, ammonia: 0.50, methane: 0.20 },
  3: { ammonia: 0.20, methane: 0.50, helium3: 0.30 },
  4: { methane: 0.30, helium3: 0.70 },
};

export function getIceWeightsForConstellationTier(constellationTier: number): Partial<Record<IceType, number>> {
  return ICE_TIER_WEIGHTS[constellationTier] || ICE_TIER_WEIGHTS[1];
}

// ==================== GAS POCKET TIER WEIGHTS ====================
// Gas pocket distribution by constellation tier (harvestable nodes inside nebulae)

export const GAS_TIER_WEIGHTS: Record<number, Partial<Record<GasType, number>>> = {
  1: { hydrogen: 0.80, nitrogen: 0.20 },
  2: { hydrogen: 0.30, nitrogen: 0.50, xenon: 0.20 },
  3: { nitrogen: 0.20, xenon: 0.50, antimatter: 0.30 },
  4: { xenon: 0.30, antimatter: 0.70 },
};

export function getGasWeightsForConstellationTier(constellationTier: number): Partial<Record<GasType, number>> {
  return GAS_TIER_WEIGHTS[constellationTier] || GAS_TIER_WEIGHTS[1];
}

// ==================== WRECKAGE FIELD TIER WEIGHTS ====================
// Wreckage drops refined materials directly (skips refinery step) — rarer than ore.

export const WRECKAGE_TIER_WEIGHTS: Record<number, Partial<Record<RefinedType, number>>> = {
  1: { steel_alloy: 0.85, cryo_bronze: 0.15 },
  2: { steel_alloy: 0.40, cryo_bronze: 0.45, carbide_plate: 0.15 },
  3: { steel_alloy: 0.10, cryo_bronze: 0.30, carbide_plate: 0.45, quantum_alloy: 0.15 },
  4: { cryo_bronze: 0.10, carbide_plate: 0.40, quantum_alloy: 0.50 },
};

export function getWreckageWeightsForConstellationTier(constellationTier: number): Partial<Record<RefinedType, number>> {
  return WRECKAGE_TIER_WEIGHTS[constellationTier] || WRECKAGE_TIER_WEIGHTS[1];
}

// ==================== ORGANIC TIER WEIGHTS ====================
// Organic resource distribution per constellation tier (planets in higher-tier constellations
// yield higher-tier organics). Used when seeding ORGANIC_T* tiles on first landing.

export const ORGANIC_TIER_WEIGHTS: Record<number, Partial<Record<OrganicType, number>>> = {
  1: { biomass: 0.80, polymers: 0.20 },
  2: { biomass: 0.30, polymers: 0.50, nanocarbon: 0.20 },
  3: { polymers: 0.20, nanocarbon: 0.50, exotic_compounds: 0.30 },
  4: { nanocarbon: 0.30, exotic_compounds: 0.70 },
};

export function getOrganicWeightsForConstellationTier(constellationTier: number): Partial<Record<OrganicType, number>> {
  return ORGANIC_TIER_WEIGHTS[constellationTier] || ORGANIC_TIER_WEIGHTS[1];
}

// ==================== CRYSTAL TIER WEIGHTS ====================
// Crystal resource distribution per constellation tier. Mirrors organics, but
// generator places crystals deeper (DEEP/CORE layers favor crystals).

export const CRYSTAL_TIER_WEIGHTS: Record<number, Partial<Record<CrystalType, number>>> = {
  1: { quartz: 0.80, silicates: 0.20 },
  2: { quartz: 0.30, silicates: 0.50, rare_crystals: 0.20 },
  3: { silicates: 0.20, rare_crystals: 0.50, singularity_shards: 0.30 },
  4: { rare_crystals: 0.30, singularity_shards: 0.70 },
};

export function getCrystalWeightsForConstellationTier(constellationTier: number): Partial<Record<CrystalType, number>> {
  return CRYSTAL_TIER_WEIGHTS[constellationTier] || CRYSTAL_TIER_WEIGHTS[1];
}

// ==================== SOLAR TIER WEIGHTS ====================
// Solar resource distribution per constellation tier (suns in higher-tier constellations
// yield higher-tier solar materials). Harvested inside the radiation zone.

export const SOLAR_TIER_WEIGHTS: Record<number, Partial<Record<SolarType, number>>> = {
  1: { solar_plasma: 0.80, charged_ions: 0.20 },
  2: { solar_plasma: 0.30, charged_ions: 0.50, fusion_matter: 0.20 },
  3: { charged_ions: 0.20, fusion_matter: 0.50, stellar_core: 0.30 },
  4: { fusion_matter: 0.30, stellar_core: 0.70 },
};

export function getSolarWeightsForConstellationTier(constellationTier: number): Partial<Record<SolarType, number>> {
  return SOLAR_TIER_WEIGHTS[constellationTier] || SOLAR_TIER_WEIGHTS[1];
}

// ==================== HARVESTABLE WEIGHT HELPERS ====================

/**
 * Pick a resource key from a weight map using uniform random.
 * Returns the first key if all weights are zero (fallback).
 */
export function pickWeightedResource<K extends string>(weights: Partial<Record<K, number>>): K {
  const entries = Object.entries(weights) as [K, number][];
  const total = entries.reduce((acc, [, w]) => acc + (w || 0), 0);
  if (total <= 0) return entries[0][0];
  let r = Math.random() * total;
  for (const [key, w] of entries) {
    r -= w || 0;
    if (r <= 0) return key;
  }
  return entries[entries.length - 1][0];
}
